import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  { name: "customer_name", label: "Nome e Cognome", type: "text", testid: "checkout-name-input" },
  { name: "email", label: "Email", type: "email", testid: "checkout-email-input" },
  { name: "address", label: "Indirizzo", type: "text", testid: "checkout-address-input" },
  { name: "city", label: "Città", type: "text", testid: "checkout-city-input" },
  { name: "zip_code", label: "CAP", type: "text", testid: "checkout-zip-input" },
];

export default function CheckoutModal() {
  const { items, checkoutOpen, setCheckoutOpen, total, clearCart, clubUnlocked } = useCart();
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    address: "",
    city: "",
    zip_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({
          product_id: i.id,
          name: i.name,
          price: i.price,
          size: i.size,
          color: i.color || "",
          qty: i.qty,
        })),
        total,
        order_type: clubUnlocked ? "club" : "guest",
      });
      setDone(data);
      clearCart();
      toast.success("Ordine dimostrativo registrato");
    } catch {
      toast.error("Errore durante l'ordine. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setCheckoutOpen(false);
    setTimeout(() => setDone(null), 300);
  };

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4"
        >
          <motion.div
            data-testid="demo-checkout-modal"
            initial={{ scale: 0.95, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.97, y: 12, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#C5A059]/20">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                Checkout Dimostrativo
                {clubUnlocked && (
                  <span data-testid="checkout-club-badge" className="ml-3 align-middle font-mono text-[10px] uppercase tracking-[0.2em] text-[#9E1B1B] border border-[#9E1B1B]/50 px-2 py-1">
                    Ordine del Club
                  </span>
                )}
              </h3>
              <button
                data-testid="checkout-close-button"
                onClick={close}
                className="p-2 text-[#C0B9B0] hover:text-[#C5A059]"
                aria-label="Chiudi"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="order-success">
                <CheckCircle2 className="w-14 h-14 text-[#C5A059] mx-auto" />
                <h4 className="mt-4 font-display text-2xl font-bold text-[#F4EBE1]">
                  Ordine registrato
                </h4>
                <p className="mt-3 text-sm text-[#C0B9B0] leading-relaxed">
                  La tua richiesta <span className="font-mono text-[#C5A059]">{done.id}</span> è
                  stata salvata come ordine dimostrativo. Nessun addebito è stato effettuato.
                </p>
                <p data-testid="order-email-notice" className="mt-2 text-xs text-[#C5A059]">
                  {done.confirmation_email_sent
                    ? "Ti abbiamo inviato un'email di riepilogo."
                    : "Riepilogo email non disponibile al momento."}
                </p>
                <button
                  data-testid="order-success-close"
                  onClick={close}
                  className="mt-6 px-8 py-3 bg-[#C5A059] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold"
                >
                  Chiudi
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {FIELDS.map((f, i) => (
                    <div key={f.name} className={i < 3 ? "col-span-2" : ""}>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
                        {f.label}
                      </label>
                      <input
                        data-testid={f.testid}
                        required
                        type={f.type}
                        value={form[f.name]}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] transition-colors"
                      />
                    </div>
                  ))}
                </div>

                <div className="leather-card p-4 space-y-2">
                  {items.map((i) => (
                    <div key={`${i.id}-${i.size}-${i.color}`} className="flex justify-between text-xs text-[#C0B9B0]">
                      <span>
                        {i.qty}× {i.name} ({i.size}{i.color ? `, ${i.color}` : ""})
                      </span>
                      <span className="font-mono">€ {(i.qty * i.price).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-[#C5A059]/20">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#78746D]">
                      Totale
                    </span>
                    <span className="font-display text-xl font-bold text-[#C5A059]">
                      € {total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3" data-testid="payment-methods-demo">
                  <div className="flex-1 border border-dashed border-[#C5A059]/40 p-3 text-center">
                    <CreditCard className="w-5 h-5 text-[#78746D] mx-auto" />
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">
                      Stripe · in arrivo
                    </p>
                  </div>
                  <div className="flex-1 border border-dashed border-[#C5A059]/40 p-3 text-center">
                    <Wallet className="w-5 h-5 text-[#78746D] mx-auto" />
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">
                      PayPal · in arrivo
                    </p>
                  </div>
                </div>

                <button
                  data-testid="place-order-button"
                  type="submit"
                  disabled={loading || items.length === 0}
                  className="w-full py-4 bg-[#9E1B1B] hover:bg-[#C5A059] text-[#F4EBE1] hover:text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-40"
                >
                  {loading ? "Elaborazione..." : "Conferma Ordine Demo"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
